import React, { useState, useEffect, useRef } from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import { exportToPdf } from '../services/reportExportService';

// Types for geographic and visitor data
interface GeoLocation {
    country: string;
    countryCode: string;
    visitors: number;
    lat?: number;
    lng?: number;
}

interface RecentVisitor {
    sessionId: string;
    pageUrl: string;
    country: string;
    city: string;
    browser: string;
    device: string;
    timestamp: string;
}

interface TrafficSource {
    source: string;
    visitors: number;
    percentage: number;
    color?: string;
}

interface AnalyticsDashboardProps {
    theme?: 'light' | 'dark';
    language?: 'en' | 'sv';
}

const translations = {
    en: {
        title: 'AI Analytics',
        subtitle: 'Google Analytics & Yandex Metrika',
        aiInsights: 'AI INSIGHTS',
        viewAllInsights: 'View all insights',
        activeNow: 'Real-time Users',
        visitors: 'Visitors',
        pageViews: 'Page Views',
        sessions: 'Sessions',
        bounceRate: 'Bounce Rate',
        avgDuration: 'Avg. Duration',
        conversionGoal: 'Conversion Goal',
        trafficSources: 'Traffic Sources',
        trafficPerformance: 'Traffic Performance',
        topPages: 'Top Pages',
        geoDistribution: 'Geographic Distribution',
        devices: 'Devices',
        browsers: 'Browsers',
        conversionFunnel: 'Conversion Funnel',
        crossPlatformKPIs: 'Cross-Platform KPIs',
        platformSynergy: 'Platform Synergy',
        aiRecommendations: 'AI Recommendations',
        executiveSummary: 'Executive Summary',
        askQuestion: 'Ask a question about your analytics...',
        generate: 'Generate',
        last7Days: 'Last 7 Days',
        last30Days: 'Last 30 Days',
        today: 'Today',
        loading: 'Loading analytics...',
        vsLastPeriod: 'vs last period',
        googleAnalytics: 'Google Analytics',
        yandexMetrika: 'Yandex Metrika',
        combinedView: 'Combined View',
        metric: 'Metric',
        variance: 'Variance',
        live: 'LIVE'
    },
    sv: {
        title: 'AI Analys',
        subtitle: 'Google Analytics & Yandex Metrika',
        aiInsights: 'AI INSIKTER',
        viewAllInsights: 'Visa alla insikter',
        activeNow: 'Realtidsanvändare',
        visitors: 'Besökare',
        pageViews: 'Sidvisningar',
        sessions: 'Sessioner',
        bounceRate: 'Avvisningsfrekvens',
        avgDuration: 'Genomsn. Varaktighet',
        conversionGoal: 'Konverteringsmål',
        trafficSources: 'Trafikkällor',
        trafficPerformance: 'Trafikprestanda',
        topPages: 'Topp Sidor',
        geoDistribution: 'Geografisk Fördelning',
        devices: 'Enheter',
        browsers: 'Webbläsare',
        conversionFunnel: 'Konverteringstratt',
        crossPlatformKPIs: 'Plattformsövergripande KPI:er',
        platformSynergy: 'Plattformssynergi',
        aiRecommendations: 'AI Rekommendationer',
        executiveSummary: 'Sammanfattning',
        askQuestion: 'Ställ en fråga om din analys...',
        generate: 'Generera',
        last7Days: 'Senaste 7 Dagar',
        last30Days: 'Senaste 30 Dagar',
        today: 'Idag',
        loading: 'Laddar analys...',
        vsLastPeriod: 'mot förra perioden',
        googleAnalytics: 'Google Analytics',
        yandexMetrika: 'Yandex Metrika',
        combinedView: 'Kombinerad Vy',
        metric: 'Mätvärde',
        variance: 'Avvikelse',
        live: 'LIVE'
    }
};

// Helper function to get country flag emoji from country code
const getCountryFlag = (countryCode: string): string => {
    if (!countryCode || countryCode.length !== 2) return '🌍';
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 0x1F1E6 - 65 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
};

// Helper function to format relative time
const formatTimeAgo = (timestamp: string, language: string): string => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (language === 'sv') {
        if (diffMins < 1) return 'Just nu';
        if (diffMins < 60) return `${diffMins} min sedan`;
        if (diffHours < 24) return `${diffHours} tim sedan`;
        return `${diffDays} dagar sedan`;
    } else {
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffHours < 24) return `${diffHours} hr ago`;
        return `${diffDays} days ago`;
    }
};

const COLORS = ['#4285F4', '#34A853', '#EA4335', '#FBBC05', '#9C27B0', '#FF5722'];

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
    theme = 'dark',
    language = 'en'
}) => {
    const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'custom'>('week');
    const [viewMode, setViewMode] = useState<'combined' | 'ga' | 'ym'>('combined');
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [realtimeUsers, setRealtimeUsers] = useState(0);
    const [excludeBots, setExcludeBots] = useState(false);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Geographic and Recent Visitors State
    const [geoData, setGeoData] = useState<GeoLocation[]>([]);
    const [recentVisitors, setRecentVisitors] = useState<RecentVisitor[]>([]);
    const [trafficSources, setTrafficSources] = useState<TrafficSource[]>([]);
    const mapRef = useRef<HTMLDivElement>(null);
    const dashboardRef = useRef<HTMLDivElement>(null);

    // New widget data
    const [funnelData, setFunnelData] = useState<any>(null);
    const [userFlowData, setUserFlowData] = useState<any>(null);
    const [heatmapData, setHeatmapData] = useState<any>(null);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [liveActivity, setLiveActivity] = useState<any[]>([]);

    // AI Features State
    const [insights, setInsights] = useState<any[]>([]);
    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [summary, setSummary] = useState('');
    const [kpis, setKpis] = useState<any[]>([]);
    const [question, setQuestion] = useState('');
    const [aiAnswer, setAiAnswer] = useState('');
    const [askingAI, setAskingAI] = useState(false);

    const isDark = theme === 'dark';
    const t = translations[language] || translations.sv;

    // Theme classes
    const bgCard = isDark
        ? 'bg-slate-800/80 backdrop-blur-sm border border-slate-700/50'
        : 'bg-white/90 backdrop-blur-sm border border-gray-200/50 shadow-sm';
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-slate-400' : 'text-gray-500';

    // Build query string for API calls
    const buildQueryParams = () => {
        const params = new URLSearchParams();
        params.set('period', period);
        if (excludeBots) params.set('excludeBots', 'true');
        if (period === 'custom' && customStartDate && customEndDate) {
            params.set('startDate', new Date(customStartDate).toISOString());
            params.set('endDate', new Date(customEndDate + 'T23:59:59').toISOString());
        }
        return params.toString();
    };

    // PDF Export — uses shared branded service
    const handleExportPdf = async () => {
        setExportingPdf(true);
        try {
            await exportToPdf('analytics-dashboard-root', t.title, summary || undefined);
        } catch (err) {
            console.error('PDF export error:', err);
        } finally {
            setExportingPdf(false);
        }
    };

    // Fetch dashboard data
    useEffect(() => {
        if (period === 'custom' && (!customStartDate || !customEndDate)) return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const qs = buildQueryParams();
                const [dashRes, insightsRes, recsRes, summaryRes, kpisRes, geoRes, visitorsRes, funnelRes, flowRes, heatmapRes] = await Promise.all([
                    fetch(`/api/analytics/dashboard?${qs}`),
                    fetch(`/api/analytics/ai/insights?period=${period}`),
                    fetch(`/api/analytics/ai/recommendations?period=${period}`),
                    fetch(`/api/analytics/ai/summary?period=${period}&language=${language}`),
                    fetch('/api/analytics/ai/kpis'),
                    fetch(`/api/analytics/geo-map?period=${period}`),
                    fetch('/api/analytics/recent-visitors?limit=10'),
                    fetch(`/api/analytics/funnel?${qs}`),
                    fetch(`/api/analytics/user-flow?${qs}`),
                    fetch(`/api/analytics/hourly-heatmap?${qs}`)
                ]);

                const [dashData, insightsData, recsData, summaryData, kpisData, geoDataRes, visitorsData, funnelDataRes, flowDataRes, heatmapDataRes] = await Promise.all([
                    dashRes.json(), insightsRes.json(), recsRes.json(), summaryRes.json(), kpisRes.json(),
                    geoRes.json(), visitorsRes.json(), funnelRes.json(), flowRes.json(), heatmapRes.json()
                ]);

                if (dashData.success) {
                    setData(dashData);
                    setRealtimeUsers(dashData.realtime?.activeUsers || 0);
                    if (dashData.sources?.sources) {
                        const sourceColors: Record<string, string> = {
                            'Organic': '#34A853',
                            'Direct': '#4285F4',
                            'Social': '#EA4335',
                            'Referral': '#FBBC05',
                            'Other': '#9C27B0'
                        };
                        setTrafficSources(dashData.sources.sources.map((s: any) => ({
                            ...s,
                            color: sourceColors[s.source] || '#64748b'
                        })));
                    }
                }
                if (insightsData.success) setInsights(insightsData.insights || []);
                if (recsData.success) setRecommendations(recsData.recommendations || []);
                if (summaryData.success) setSummary(summaryData.summary || '');
                if (kpisData.success) setKpis(kpisData.metrics || []);
                if (geoDataRes.success) setGeoData(geoDataRes.countries || []);
                if (visitorsData.success) setRecentVisitors(visitorsData.visitors || []);
                if (funnelDataRes.success) setFunnelData(funnelDataRes);
                if (flowDataRes.success) setUserFlowData(flowDataRes);
                if (heatmapDataRes.success) setHeatmapData(heatmapDataRes);
            } catch (err) {
                console.error('Analytics fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [period, language, excludeBots, customStartDate, customEndDate]);

    // Realtime update every 30 seconds
    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const res = await fetch('/api/analytics/realtime');
                const json = await res.json();
                if (json.success) setRealtimeUsers(json.activeUsers);
            } catch (err) { /* ignore */ }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    // Live activity refresh every 15 seconds
    useEffect(() => {
        const fetchLive = async () => {
            try {
                const res = await fetch('/api/analytics/live-activity?limit=15');
                const json = await res.json();
                if (json.success) setLiveActivity(json.activities || []);
            } catch (err) { /* ignore */ }
        };
        fetchLive();
        const interval = setInterval(fetchLive, 15000);
        return () => clearInterval(interval);
    }, []);

    // Ask AI Question
    const handleAskQuestion = async () => {
        if (!question.trim()) return;
        setAskingAI(true);
        try {
            const res = await fetch('/api/analytics/ai/ask', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question, period, language })
            });
            const data = await res.json();
            if (data.success) setAiAnswer(data.answer);
        } catch (err) {
            setAiAnswer(language === 'sv' ? 'Kunde inte generera svar.' : 'Could not generate answer.');
        } finally {
            setAskingAI(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" />
                <span className={`ml-3 ${textSecondary}`}>{t.loading}</span>
            </div>
        );
    }

    const traffic = data?.traffic || { data: [], totals: {} };
    const sources = data?.sources?.sources || [];
    const devices = data?.devices?.devices || [];
    const conversions = data?.conversions || { funnel: [] };

    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Radar chart data for platform synergy
    const radarData = [
        { metric: 'Reach', ga: 80, ym: 75 },
        { metric: 'Conversion', ga: 65, ym: 60 },
        { metric: 'Retention', ga: 70, ym: 72 },
        { metric: 'Stickiness', ga: 55, ym: 58 }
    ];

    return (
        <div className="space-y-6" ref={dashboardRef} id="analytics-dashboard-root">
            {/* Header with Platform Selector */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h2 className={`text-2xl font-bold ${textPrimary}`}>{t.title}</h2>
                    <p className={`text-sm ${textSecondary}`}>{t.subtitle}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Bot Exclusion Toggle */}
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-xl cursor-pointer ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                        <span className="text-sm">🤖</span>
                        <span className={`text-xs ${textSecondary}`}>{language === 'sv' ? 'Exkl. bottar' : 'Excl. bots'}</span>
                        <div className="relative">
                            <input
                                type="checkbox"
                                checked={excludeBots}
                                onChange={(e) => setExcludeBots(e.target.checked)}
                                className="sr-only"
                            />
                            <div className={`w-8 h-4 rounded-full transition-colors ${excludeBots ? 'bg-teal-500' : isDark ? 'bg-slate-600' : 'bg-gray-300'}`}
                                onClick={() => setExcludeBots(!excludeBots)}
                            >
                                <div className={`w-3.5 h-3.5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${excludeBots ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </div>
                        </div>
                    </label>
                    {/* PDF Export */}
                    <button
                        onClick={handleExportPdf}
                        disabled={exportingPdf}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} ${textSecondary}`}
                    >
                        {exportingPdf ? (
                            <span className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span>📄</span>
                        )}
                        PDF
                    </button>
                    {/* Platform Toggle */}
                    <div className={`flex p-1 rounded-xl ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                        {[
                            { id: 'ga', label: t.googleAnalytics },
                            { id: 'ym', label: t.yandexMetrika },
                            { id: 'combined', label: t.combinedView }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setViewMode(tab.id as any)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${viewMode === tab.id
                                    ? 'bg-teal-500 text-white'
                                    : `${textSecondary} hover:${textPrimary}`
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    {/* Period Toggle */}
                    <div className={`flex p-1 rounded-xl ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                        {[
                            { id: 'today', label: t.today },
                            { id: 'week', label: t.last7Days },
                            { id: 'month', label: t.last30Days },
                            { id: 'custom', label: language === 'sv' ? 'Anpassad' : 'Custom' }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setPeriod(tab.id as any)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${period === tab.id
                                    ? 'bg-purple-500 text-white'
                                    : `${textSecondary} hover:${textPrimary}`
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Custom Date Range Picker */}
            {period === 'custom' && (
                <div className={`flex items-center gap-4 p-4 rounded-xl ${isDark ? 'bg-slate-800/60 border border-slate-700' : 'bg-white border border-gray-200'}`}>
                    <span className={`text-sm font-medium ${textSecondary}`}>📅 {language === 'sv' ? 'Datumintervall:' : 'Date range:'}</span>
                    <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        max={customEndDate || undefined}
                        className={`px-3 py-1.5 rounded-lg text-sm border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                    <span className={textSecondary}>→</span>
                    <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        min={customStartDate || undefined}
                        max={new Date().toISOString().split('T')[0]}
                        className={`px-3 py-1.5 rounded-lg text-sm border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                    {customStartDate && customEndDate && (
                        <span className={`text-xs ${textSecondary}`}>
                            {Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24))} {language === 'sv' ? 'dagar' : 'days'}
                        </span>
                    )}
                </div>
            )}

            {/* AI Insights Section */}
            <div className={`p-5 rounded-2xl ${isDark ? 'bg-gradient-to-r from-slate-800 to-slate-700' : 'bg-gradient-to-r from-blue-50 to-indigo-50'} border ${isDark ? 'border-slate-600' : 'border-blue-100'}`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <span className="text-lg">✨</span>
                        <span className={`text-xs font-bold tracking-wider ${textSecondary}`}>{t.aiInsights}</span>
                    </div>
                    <button className="text-sm text-teal-500 hover:text-teal-400">{t.viewAllInsights}</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {insights.slice(0, 3).map((insight, i) => (
                        <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-slate-800/50' : 'bg-white/80'} flex gap-3`}>
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: insight.color + '20' }}>
                                <span className="material-icons-round" style={{ color: insight.color }}>{insight.icon}</span>
                            </div>
                            <div className="flex-1">
                                <h4 className={`font-bold text-sm ${textPrimary}`}>{insight.title[language]}</h4>
                                <p className={`text-xs ${textSecondary} mt-1 line-clamp-2`}>{insight.description[language]}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Real-time Users */}
                <div className={`p-5 rounded-2xl ${bgCard} relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-24 h-24 bg-green-500/10 rounded-full blur-2xl" />
                    <div className="flex items-center justify-between mb-1">
                        <p className={`text-xs font-bold tracking-wider ${textSecondary}`}>{t.activeNow}</p>
                        <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full animate-pulse">{t.live}</span>
                    </div>
                    <p className="text-4xl font-black text-green-500">{(realtimeUsers ?? 0).toLocaleString()}</p>
                    <span className="text-xs text-green-400">+4% {t.vsLastPeriod}</span>
                </div>

                {/* Avg Session Duration */}
                <div className={`p-5 rounded-2xl ${bgCard}`}>
                    <p className={`text-xs font-bold tracking-wider mb-1 ${textSecondary}`}>{t.avgDuration}</p>
                    <p className="text-4xl font-black">{formatDuration(traffic.avgSessionDuration || 165)}</p>
                    <span className="text-xs text-green-500">+12s {t.vsLastPeriod}</span>
                </div>

                {/* Bounce Rate */}
                <div className={`p-5 rounded-2xl ${bgCard}`}>
                    <p className={`text-xs font-bold tracking-wider mb-1 ${textSecondary}`}>{t.bounceRate}</p>
                    <p className="text-4xl font-black">{traffic.avgBounceRate || 42.1}%</p>
                    <span className="text-xs text-green-500">+0.8% {t.vsLastPeriod}</span>
                </div>

                {/* Conversion Goal */}
                <div className={`p-5 rounded-2xl ${bgCard}`}>
                    <p className={`text-xs font-bold tracking-wider mb-1 ${textSecondary}`}>{t.conversionGoal}</p>
                    <p className="text-4xl font-black">{conversions.conversionRate || 3.8}%</p>
                    <span className="text-xs text-green-500">+1.2% {t.vsLastPeriod}</span>
                    <div className="mt-2 h-2 bg-slate-700/50 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full" style={{ width: `${Math.min((conversions.conversionRate || 3.8) * 20, 100)}%` }} />
                    </div>
                </div>
            </div>

            {/* Traffic Performance Chart */}
            <div className={`p-6 rounded-2xl ${bgCard}`}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className={`font-bold text-lg ${textPrimary}`}>{t.trafficPerformance}</h3>
                        <p className={`text-xs ${textSecondary}`}>Comparison of Google Analytics vs Yandex Metrika data sources</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-[#4285F4]" />
                            <span className={`text-xs ${textSecondary}`}>Google</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-[#EA4335]" />
                            <span className={`text-xs ${textSecondary}`}>Yandex</span>
                        </div>
                    </div>
                </div>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={traffic.data}>
                            <defs>
                                <linearGradient id="colorGA" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#4285F4" stopOpacity={0.2} />
                                    <stop offset="95%" stopColor="#4285F4" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorYM" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#EA4335" stopOpacity={0.2} />
                                    <stop offset="95%" stopColor="#EA4335" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} vertical={false} />
                            <XAxis dataKey="date" stroke={isDark ? '#64748b' : '#94a3b8'} fontSize={11} tickLine={false} />
                            <YAxis stroke={isDark ? '#64748b' : '#94a3b8'} fontSize={11} tickLine={false} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1e293b' : '#fff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                                }}
                            />
                            <Area type="monotone" dataKey="visitors" stroke="#4285F4" strokeWidth={2} fill="url(#colorGA)" name="GA" />
                            <Area type="monotone" dataKey="sessions" stroke="#EA4335" strokeWidth={2} strokeDasharray="5 5" fill="url(#colorYM)" name="YM" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Geographic Distribution & Traffic Sources Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Geographic Map */}
                <div className={`p-6 rounded-2xl ${bgCard} lg:col-span-2`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <span className="text-xl">🌍</span>
                            <h3 className={`font-bold text-lg ${textPrimary}`}>{t.geoDistribution}</h3>
                        </div>
                        <span className={`text-xs ${textSecondary}`}>{geoData.length} countries</span>
                    </div>

                    {geoData.length > 0 ? (
                        <div className="flex gap-4">
                            {/* Simple country list with visitor counts */}
                            <div className="flex-1 space-y-2 max-h-64 overflow-y-auto pr-2">
                                {geoData.slice(0, 10).map((country, i) => (
                                    <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                                        <div className="flex items-center gap-3">
                                            <span className="text-lg">{getCountryFlag(country.countryCode)}</span>
                                            <span className={`text-sm font-medium ${textPrimary}`}>{country.country}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-sm font-bold ${textPrimary}`}>{(country.visitors ?? 0).toLocaleString()}</span>
                                            <span className={`text-xs ${textSecondary}`}>{language === 'sv' ? 'besökare' : 'visitors'}</span>
                                        </div>
                                    </div>
                                ))}
                                {geoData.length === 0 && (
                                    <div className={`text-center py-8 ${textSecondary}`}>
                                        {language === 'sv' ? 'Ingen geografisk data ännu' : 'No geographic data yet'}
                                    </div>
                                )}
                            </div>

                            {/* Visual bar representation */}
                            <div className="w-48 space-y-2">
                                {geoData.slice(0, 5).map((country, i) => {
                                    const maxVisitors = Math.max(...geoData.map(c => c.visitors));
                                    const percentage = (country.visitors / maxVisitors) * 100;
                                    return (
                                        <div key={i} className="space-y-1">
                                            <div className="flex justify-between text-xs">
                                                <span className={textSecondary}>{country.countryCode}</span>
                                                <span className={textPrimary}>{Math.round((country.visitors / geoData.reduce((acc, c) => acc + c.visitors, 0)) * 100)}%</span>
                                            </div>
                                            <div className={`h-2 rounded-full ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                                <div
                                                    className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className={`text-center py-8 ${textSecondary}`}>
                            {language === 'sv' ? 'Laddar geografisk data...' : 'Loading geographic data...'}
                        </div>
                    )}
                </div>

                {/* Traffic Sources */}
                <div className={`p-6 rounded-2xl ${bgCard}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-xl">📊</span>
                        <h3 className={`font-bold text-lg ${textPrimary}`}>{t.trafficSources}</h3>
                    </div>

                    {trafficSources.length > 0 ? (
                        <div className="flex gap-6 items-center">
                            {/* Donut Chart */}
                            <div className="w-40 h-40 flex-shrink-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={trafficSources}
                                            dataKey="visitors"
                                            nameKey="source"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={35}
                                            outerRadius={60}
                                            strokeWidth={2}
                                            stroke={isDark ? '#1e293b' : '#fff'}
                                        >
                                            {trafficSources.map((entry, index) => (
                                                <Cell key={index} fill={entry.color || COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: isDark ? '#1e293b' : '#fff',
                                                border: 'none',
                                                borderRadius: '12px',
                                                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                                            }}
                                            formatter={(value: number, name: string) => [`${value.toLocaleString()} visitors`, name]}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            {/* Legend List */}
                            <div className="flex-1 space-y-3">
                                {trafficSources.map((source, i) => (
                                    <div key={i} className="space-y-1">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: source.color }} />
                                                <span className={`text-sm ${textPrimary}`}>{source.source}</span>
                                            </div>
                                            <span className={`text-sm font-bold ${textPrimary}`}>{(source.percentage ?? 0).toFixed(1)}%</span>
                                        </div>
                                        <div className={`h-1.5 rounded-full ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                            <div
                                                className="h-full rounded-full transition-all"
                                                style={{ width: `${source.percentage}%`, backgroundColor: source.color }}
                                            />
                                        </div>
                                        <div className={`text-xs ${textSecondary}`}>
                                            {(source.visitors ?? 0).toLocaleString()} {language === 'sv' ? 'besökare' : 'visitors'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className={`text-center py-8 ${textSecondary}`}>
                            {language === 'sv' ? 'Ingen trafikdata ännu' : 'No traffic data yet'}
                        </div>
                    )}
                </div>
            </div>

            {/* Device & Browser Distribution */}
            {devices.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Devices Donut */}
                    <div className={`p-6 rounded-2xl ${bgCard}`}>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-xl">📱</span>
                            <h3 className={`font-bold text-lg ${textPrimary}`}>{t.devices}</h3>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="w-48 h-48">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={devices}
                                            dataKey="sessions"
                                            nameKey="device"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={40}
                                            outerRadius={70}
                                            strokeWidth={2}
                                            stroke={isDark ? '#1e293b' : '#fff'}
                                        >
                                            {devices.map((_: any, index: number) => (
                                                <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: isDark ? '#1e293b' : '#fff',
                                                border: 'none',
                                                borderRadius: '12px',
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex-1 space-y-2">
                                {devices.map((d: any, i: number) => (
                                    <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'bg-slate-700/40' : 'bg-gray-50'}`}>
                                        <div className="flex items-center gap-2">
                                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                            <span className={`text-sm font-medium ${textPrimary}`}>{d.device}</span>
                                        </div>
                                        <span className={`text-sm font-bold ${textPrimary}`}>{d.sessions?.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Browsers Bar Chart */}
                    <div className={`p-6 rounded-2xl ${bgCard}`}>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-xl">🌐</span>
                            <h3 className={`font-bold text-lg ${textPrimary}`}>{t.browsers}</h3>
                        </div>
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={devices.flatMap((d: any) => d.browsers || []).slice(0, 6)} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} horizontal={false} />
                                    <XAxis type="number" stroke={isDark ? '#64748b' : '#94a3b8'} fontSize={11} />
                                    <YAxis dataKey="browser" type="category" stroke={isDark ? '#64748b' : '#94a3b8'} fontSize={11} width={80} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1e293b' : '#fff',
                                            border: 'none',
                                            borderRadius: '12px',
                                        }}
                                    />
                                    <Bar dataKey="sessions" fill="#4285F4" radius={[0, 6, 6, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent Visitors */}
            <div className={`p-6 rounded-2xl ${bgCard}`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <span className="text-xl">👥</span>
                        <h3 className={`font-bold text-lg ${textPrimary}`}>
                            {language === 'sv' ? 'Senaste Besökare' : 'Recent Visitors'}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${isDark ? 'bg-teal-500/20 text-teal-400' : 'bg-teal-100 text-teal-700'}`}>
                            {t.live}
                        </span>
                    </div>
                    <span className={`text-xs ${textSecondary}`}>
                        {recentVisitors.length} {language === 'sv' ? 'senaste besök' : 'recent visits'}
                    </span>
                </div>

                {recentVisitors.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={isDark ? 'bg-slate-700/30' : 'bg-gray-50'}>
                                <tr className="text-left text-xs uppercase tracking-wider">
                                    <th className={`px-4 py-3 font-semibold ${textSecondary}`}>{language === 'sv' ? 'Plats' : 'Location'}</th>
                                    <th className={`px-4 py-3 font-semibold ${textSecondary}`}>{language === 'sv' ? 'Sida' : 'Page'}</th>
                                    <th className={`px-4 py-3 font-semibold ${textSecondary}`}>{language === 'sv' ? 'Enhet' : 'Device'}</th>
                                    <th className={`px-4 py-3 font-semibold ${textSecondary}`}>{language === 'sv' ? 'Webbläsare' : 'Browser'}</th>
                                    <th className={`px-4 py-3 font-semibold text-right ${textSecondary}`}>{language === 'sv' ? 'Tid' : 'Time'}</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-gray-100'}`}>
                                {recentVisitors.map((visitor, i) => (
                                    <tr key={i} className={`${isDark ? 'hover:bg-slate-700/20' : 'hover:bg-gray-50'} transition-colors`}>
                                        <td className={`px-4 py-3 ${textPrimary}`}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm">{getCountryFlag(visitor.country)}</span>
                                                <span className="text-sm">{visitor.city || visitor.country}</span>
                                            </div>
                                        </td>
                                        <td className={`px-4 py-3 ${textSecondary}`}>
                                            <span className="text-sm truncate max-w-[200px] block">{visitor.pageUrl}</span>
                                        </td>
                                        <td className={`px-4 py-3 ${textSecondary}`}>
                                            <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`}>
                                                {visitor.device}
                                            </span>
                                        </td>
                                        <td className={`px-4 py-3 ${textSecondary}`}>
                                            <span className="text-sm">{visitor.browser}</span>
                                        </td>
                                        <td className={`px-4 py-3 text-right ${textSecondary}`}>
                                            <span className="text-sm">{formatTimeAgo(visitor.timestamp, language)}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={`text-center py-8 ${textSecondary}`}>
                        {language === 'sv' ? 'Inga besökare registrerade ännu' : 'No visitors recorded yet'}
                    </div>
                )}
            </div>

            {/* Live Activity Feed */}
            {liveActivity.length > 0 && (
                <div className={`p-6 rounded-2xl ${bgCard}`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <span className="text-xl">⚡</span>
                            <h3 className={`font-bold text-lg ${textPrimary}`}>
                                {language === 'sv' ? 'Aktiv Aktivitet' : 'Live Activity Feed'}
                            </h3>
                            <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'} animate-pulse`}>
                                {liveActivity.filter(a => a.status === 'active').length} {language === 'sv' ? 'aktiva' : 'active'}
                            </span>
                        </div>
                        <span className={`text-xs ${textSecondary}`}>
                            {language === 'sv' ? 'Uppdateras var 15:e sek' : 'Updates every 15s'}
                        </span>
                    </div>

                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                        {liveActivity.map((session, i) => (
                            <div
                                key={session.sessionId || i}
                                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-gray-50 hover:bg-gray-100'}`}
                            >
                                {/* Status indicator */}
                                <div className="flex flex-col items-center gap-1">
                                    <div className={`w-2.5 h-2.5 rounded-full ${session.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
                                    <span className={`text-[9px] ${textSecondary}`}>
                                        {session.status === 'active' ? '🟢' : '🟡'}
                                    </span>
                                </div>

                                {/* Location & Device */}
                                <div className="min-w-[120px]">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-sm">{getCountryFlag(session.countryCode)}</span>
                                        <span className={`text-sm font-medium ${textPrimary}`}>{session.city || session.country}</span>
                                    </div>
                                    <div className={`text-xs ${textSecondary} flex items-center gap-1 mt-0.5`}>
                                        <span>{session.device === 'mobile' ? '📱' : session.device === 'tablet' ? '📲' : '💻'}</span>
                                        <span>{session.browser}</span>
                                    </div>
                                </div>

                                {/* Current Page */}
                                <div className="flex-1 min-w-0">
                                    <div className={`text-sm font-medium ${textPrimary} truncate`}>
                                        📄 {session.currentPage}
                                    </div>
                                    {session.pageCount > 1 && (
                                        <div className={`text-xs ${textSecondary} mt-0.5`}>
                                            {session.pageCount} {language === 'sv' ? 'sidor visade' : 'pages viewed'}
                                        </div>
                                    )}
                                </div>

                                {/* Engagement Metrics */}
                                <div className="flex items-center gap-3">
                                    {/* Scroll Depth */}
                                    {session.scrollDepth > 0 && (
                                        <div className="text-center">
                                            <div className={`text-xs font-bold ${session.scrollDepth > 75 ? 'text-green-500' : session.scrollDepth > 40 ? 'text-amber-500' : 'text-red-400'}`}>
                                                {session.scrollDepth}%
                                            </div>
                                            <div className={`text-[9px] ${textSecondary}`}>scroll</div>
                                        </div>
                                    )}

                                    {/* Engagement Score */}
                                    {session.engagementScore > 0 && (
                                        <div className="text-center">
                                            <div className={`text-xs font-bold ${session.engagementScore > 60 ? 'text-teal-500' : 'text-slate-400'}`}>
                                                {session.engagementScore}
                                            </div>
                                            <div className={`text-[9px] ${textSecondary}`}>{language === 'sv' ? 'poäng' : 'score'}</div>
                                        </div>
                                    )}

                                    {/* Click Count */}
                                    {session.recentClicks.length > 0 && (
                                        <div className="text-center">
                                            <div className={`text-xs font-bold text-purple-400`}>
                                                {session.recentClicks.length}
                                            </div>
                                            <div className={`text-[9px] ${textSecondary}`}>{language === 'sv' ? 'klick' : 'clicks'}</div>
                                        </div>
                                    )}

                                    {/* Time */}
                                    <div className={`text-xs ${textSecondary} min-w-[50px] text-right`}>
                                        {formatTimeAgo(session.lastActivity, language)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Cross-Platform KPIs & Radar */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* KPIs Table */}
                <div className={`p-6 rounded-2xl ${bgCard}`}>
                    <h3 className={`font-bold text-lg mb-4 ${textPrimary}`}>{t.crossPlatformKPIs}</h3>
                    <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                        <table className="w-full">
                            <thead className={isDark ? 'bg-slate-600/30' : 'bg-gray-100'}>
                                <tr className="text-left text-xs uppercase tracking-wider">
                                    <th className={`px-4 py-3 font-semibold ${textSecondary}`}>{t.metric}</th>
                                    <th className={`px-4 py-3 font-semibold text-center ${textSecondary}`}>GA4</th>
                                    <th className={`px-4 py-3 font-semibold text-center ${textSecondary}`}>Metrika</th>
                                    <th className={`px-4 py-3 font-semibold text-right ${textSecondary}`}>{t.variance}</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDark ? 'divide-slate-600/30' : 'divide-gray-200'}`}>
                                {kpis.map((kpi: any, i: number) => (
                                    <tr key={i}>
                                        <td className={`px-4 py-3 font-medium ${textPrimary}`}>{kpi.name[language]}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`px-3 py-1 rounded-lg ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                                                {typeof kpi.ga4 === 'number' ? kpi.ga4.toLocaleString() : kpi.ga4}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`px-3 py-1 rounded-lg ${isDark ? 'bg-slate-600/50' : 'bg-gray-200'}`}>
                                                {typeof kpi.metrika === 'number' ? kpi.metrika.toLocaleString() : kpi.metrika}
                                            </span>
                                        </td>
                                        <td className={`px-4 py-3 text-right font-bold ${parseFloat(kpi.variance) >= 0 ? 'text-green-500' : 'text-red-400'}`}>
                                            {parseFloat(kpi.variance) >= 0 ? '+' : ''}{kpi.variance}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Platform Synergy Radar */}
                <div className={`p-6 rounded-2xl ${bgCard}`}>
                    <h3 className={`font-bold text-lg mb-4 ${textPrimary}`}>{t.platformSynergy}</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={radarData}>
                                <PolarGrid stroke={isDark ? '#334155' : '#e2e8f0'} />
                                <PolarAngleAxis dataKey="metric" stroke={isDark ? '#64748b' : '#94a3b8'} fontSize={11} />
                                <PolarRadiusAxis stroke={isDark ? '#334155' : '#e2e8f0'} fontSize={10} />
                                <Radar name="Google" dataKey="ga" stroke="#4285F4" fill="#4285F4" fillOpacity={0.3} strokeWidth={2} />
                                <Radar name="Metrika" dataKey="ym" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.1} strokeWidth={2} strokeDasharray="5 5" />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex items-center justify-center gap-6 mt-2">
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-[#4285F4]" />
                            <span className={`text-xs ${textSecondary}`}>Google</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-slate-400" />
                            <span className={`text-xs ${textSecondary}`}>Metrika</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Q&A Section */}
            <div className={`p-6 rounded-2xl ${bgCard}`}>
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-xl">🤖</span>
                    <h3 className={`font-bold text-lg ${textPrimary}`}>AI Report Generator</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
                        • Data Sync: 2 mins ago
                    </span>
                </div>

                {/* Question Input */}
                <div className="flex gap-3 mb-6">
                    <div className={`flex-1 flex items-center gap-3 px-4 py-3 rounded-xl ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                        <span className="text-lg">✨</span>
                        <input
                            type="text"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAskQuestion()}
                            placeholder={t.askQuestion}
                            className={`flex-1 bg-transparent outline-none ${textPrimary}`}
                        />
                        {question && (
                            <button onClick={() => setQuestion('')} className={textSecondary}>✕</button>
                        )}
                    </div>
                    <button
                        onClick={handleAskQuestion}
                        disabled={askingAI || !question.trim()}
                        className="px-6 py-3 bg-teal-500 text-white font-bold rounded-xl hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {askingAI ? (
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : null}
                        {t.generate}
                    </button>
                </div>

                {/* Executive Summary / AI Answer */}
                <div className={`p-5 rounded-xl ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'} border ${isDark ? 'border-slate-600/30' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="material-icons-round text-xl">description</span>
                            <span className={`font-bold ${textPrimary}`}>{t.executiveSummary}</span>
                        </div>
                        <span className={`text-xs ${textSecondary}`}>AI MODEL: ANALYSIS-V4</span>
                    </div>
                    <p className={`text-sm leading-relaxed ${textPrimary}`}>
                        {aiAnswer || summary || (language === 'sv'
                            ? 'Under de senaste 7 dagarna visar Google Analytics 4 och Yandex Metrika 92% korrelation. Trafiken ökade med 14% jämfört med föregående period. Konverteringsfrekvensen ligger stabilt, vilket indikerar hög användarnöjdhet.'
                            : 'Over the last 7 days, Google Analytics 4 and Yandex Metrika show 92% correlation. Traffic increased by 14% compared to the previous period. Conversion rate remains stable, indicating high user satisfaction.'
                        )}
                    </p>
                </div>
            </div>

            {/* AI Recommendations */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-xl">💡</span>
                    <h3 className={`font-bold text-lg ${textPrimary}`}>{t.aiRecommendations}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {recommendations.map((rec, i) => (
                        <div key={i} className={`p-5 rounded-2xl ${bgCard} border-l-4`} style={{ borderColor: rec.iconBg }}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: rec.iconBg + '20' }}>
                                    <span className="material-icons-round" style={{ color: rec.iconBg }}>{rec.icon}</span>
                                </div>
                                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${rec.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                                    rec.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                        'bg-green-500/20 text-green-400'
                                    }`}>
                                    PRIORITY: {rec.priority.toUpperCase()}
                                </span>
                            </div>
                            <h4 className={`font-bold ${textPrimary} mb-2`}>{rec.title[language]}</h4>
                            <p className={`text-xs ${textSecondary} leading-relaxed`}>{rec.description[language]}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Conversion Funnel */}
            {funnelData?.steps?.length > 0 && (
                <div className={`p-6 rounded-2xl ${bgCard}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-xl">🔄</span>
                        <h3 className={`font-bold text-lg ${textPrimary}`}>{t.conversionFunnel}</h3>
                        <span className={`ml-auto text-xs ${textSecondary}`}>
                            {language === 'sv' ? 'Konverteringsfrekvens' : 'Conversion rate'}: {funnelData.conversionRate}%
                        </span>
                    </div>
                    <div className="flex items-end justify-between gap-2">
                        {funnelData.steps.map((step: any, i: number) => {
                            const maxVal = funnelData.steps[0]?.value || 1;
                            const pct = Math.max((step.value / maxVal) * 100, 8);
                            const colors = ['#4285F4', '#34A853', '#FBBC05', '#EA4335', '#9C27B0'];
                            return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                                    <span className={`text-xs font-bold ${textPrimary}`}>{step.value.toLocaleString()}</span>
                                    <div
                                        className="w-full rounded-t-lg transition-all duration-500"
                                        style={{
                                            height: `${pct * 1.8}px`,
                                            backgroundColor: colors[i % colors.length],
                                            opacity: 0.85
                                        }}
                                    />
                                    <span className={`text-[10px] text-center ${textSecondary}`}>
                                        {language === 'sv' ? step.name : step.nameEn}
                                    </span>
                                    {i > 0 && (
                                        <span className="text-[9px] text-orange-400">
                                            {((step.value / (funnelData.steps[i - 1]?.value || 1)) * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* User Flow */}
            {userFlowData?.links?.length > 0 && (
                <div className={`p-6 rounded-2xl ${bgCard}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-xl">🔀</span>
                        <h3 className={`font-bold text-lg ${textPrimary}`}>{language === 'sv' ? 'Användarflöde' : 'User Flow'}</h3>
                        <span className={`ml-auto text-xs ${textSecondary}`}>
                            {userFlowData.totalSessions} {language === 'sv' ? 'sessioner' : 'sessions'}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Entry Pages */}
                        <div>
                            <h4 className={`text-xs font-bold mb-2 ${textSecondary}`}>📥 {language === 'sv' ? 'Ingångssidor' : 'Entry Pages'}</h4>
                            <div className="space-y-1">
                                {userFlowData.entryPages?.slice(0, 5).map((ep: any, i: number) => (
                                    <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-slate-700/40' : 'bg-gray-50'}`}>
                                        <span className={textPrimary}>{ep.page}</span>
                                        <span className="text-teal-500 font-bold text-xs">{ep.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Top Transitions */}
                        <div>
                            <h4 className={`text-xs font-bold mb-2 ${textSecondary}`}>🔗 {language === 'sv' ? 'Populära övergångar' : 'Top Transitions'}</h4>
                            <div className="space-y-1">
                                {userFlowData.links?.slice(0, 5).map((link: any, i: number) => (
                                    <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-slate-700/40' : 'bg-gray-50'}`}>
                                        <span className={`${textPrimary} text-xs`}>{link.source}</span>
                                        <span className="text-purple-400">→</span>
                                        <span className={`${textPrimary} text-xs`}>{link.target}</span>
                                        <span className="ml-auto text-teal-500 font-bold text-xs">{link.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Exit Pages */}
                        <div>
                            <h4 className={`text-xs font-bold mb-2 ${textSecondary}`}>📤 {language === 'sv' ? 'Utgångssidor' : 'Exit Pages'}</h4>
                            <div className="space-y-1">
                                {userFlowData.exitPages?.slice(0, 5).map((ep: any, i: number) => (
                                    <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-slate-700/40' : 'bg-gray-50'}`}>
                                        <span className={textPrimary}>{ep.page}</span>
                                        <span className="text-red-400 font-bold text-xs">{ep.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Hourly Traffic Heatmap */}
            {heatmapData?.heatmap?.length > 0 && (
                <div className={`p-6 rounded-2xl ${bgCard}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-xl">🗓️</span>
                        <h3 className={`font-bold text-lg ${textPrimary}`}>{language === 'sv' ? 'Trafik per timme' : 'Hourly Traffic'}</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(24, 1fr)', gap: '2px', fontSize: '10px' }}>
                            {/* Hour headers */}
                            <div />
                            {Array.from({ length: 24 }, (_, h) => (
                                <div key={h} className={`text-center py-1 ${textSecondary}`}>{h.toString().padStart(2, '0')}</div>
                            ))}
                            {/* Rows */}
                            {heatmapData.days?.map((day: string, di: number) => (
                                <React.Fragment key={day}>
                                    <div className={`pr-2 py-1 text-right font-medium ${textSecondary}`}>{day}</div>
                                    {Array.from({ length: 24 }, (_, h) => {
                                        const cell = heatmapData.heatmap.find((c: any) => c.dayIndex === di && c.hour === h);
                                        const val = cell?.value || 0;
                                        const maxVal = Math.max(...heatmapData.heatmap.map((c: any) => c.value), 1);
                                        const intensity = val / maxVal;
                                        return (
                                            <div
                                                key={h}
                                                title={`${day} ${h}:00 — ${val} ${language === 'sv' ? 'sidvisningar' : 'pageviews'}`}
                                                className="rounded-sm transition-colors cursor-default"
                                                style={{
                                                    backgroundColor: val === 0
                                                        ? (isDark ? '#1e293b' : '#f1f5f9')
                                                        : `rgba(20, 184, 166, ${Math.max(0.15, intensity)})`,
                                                    minHeight: '18px'
                                                }}
                                            />
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-2">
                            <span className={`text-[10px] ${textSecondary}`}>{language === 'sv' ? 'Låg' : 'Low'}</span>
                            <div className="flex gap-0.5">
                                {[0.15, 0.3, 0.5, 0.7, 0.9].map((o, i) => (
                                    <div key={i} className="w-4 h-2.5 rounded-sm" style={{ backgroundColor: `rgba(20, 184, 166, ${o})` }} />
                                ))}
                            </div>
                            <span className={`text-[10px] ${textSecondary}`}>{language === 'sv' ? 'Hög' : 'High'}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Provider Status Footer */}
            <div className={`p-4 rounded-xl ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'} flex items-center justify-center gap-6`}>
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className={`text-sm ${textSecondary}`}>Google Analytics 4</span>
                    <code className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`}>G-R4WEEBG3P1</code>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className={`text-sm ${textSecondary}`}>Yandex Metrika</span>
                    <code className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`}>106643915</code>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
